import React, { useEffect, useMemo, useRef, useState } from 'react';
import usePayment from '../hooks/usePayment';
import { dispatchPaymentSuccess } from '../utils/billingEvents';

const overlayStyle = {
	position: 'fixed',
	inset: 0,
	background: 'rgba(17, 17, 19, 0.72)',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: '24px',
	zIndex: 2000,
};

const modalStyle = {
	width: 'min(520px, 100%)',
	background: '#fff',
	borderRadius: '20px',
	padding: '24px',
	boxShadow: '0 24px 80px rgba(0, 0, 0, 0.2)',
};

const buttonRowStyle = {
	display: 'flex',
	flexWrap: 'wrap',
	gap: '12px',
	marginTop: '20px',
};

const primaryButtonStyle = {
	border: 'none',
	borderRadius: '12px',
	padding: '12px 18px',
	background: '#111113',
	color: '#fff',
	fontWeight: 700,
	cursor: 'pointer',
};

const softButtonStyle = {
	...primaryButtonStyle,
	background: '#F3F4F6',
	color: '#111113',
};

const formatNaira = (amount) => `NGN ${Number(amount || 0).toLocaleString('en-NG')}`;

const PaymentModal = ({ isOpen, eventId, initialAmountNgn = 0, onClose, onPaymentSuccess }) => {
	const { loading, error, initializePayment, verifyPayment, generatePaymentLink } = usePayment();
	const [reference, setReference] = useState('');
	const [amountNgn, setAmountNgn] = useState(initialAmountNgn);
	const [statusMessage, setStatusMessage] = useState('');
	const [whatsAppUrl, setWhatsAppUrl] = useState('');
	const popupRef = useRef(null);

	const isTrackingReference = useMemo(() => Boolean(reference), [reference]);

	useEffect(() => {
		if (isOpen) {
			setAmountNgn(initialAmountNgn);
			return;
		}
		if (!isOpen) {
			setReference('');
			setStatusMessage('');
			setWhatsAppUrl('');
			setAmountNgn(initialAmountNgn);
		}
	}, [isOpen, initialAmountNgn]);

	useEffect(() => {
		if (!isOpen || !reference) return undefined;
		const interval = setInterval(async () => {
			try {
				const verification = await verifyPayment(reference);
				if (verification?.status === 'success') {
					dispatchPaymentSuccess({ eventId, reference });
					onPaymentSuccess?.(verification);
					onClose?.();
				}
			} catch (err) {
				// Silent while polling.
			}
		}, 2500);
		return () => clearInterval(interval);
	}, [eventId, isOpen, onClose, onPaymentSuccess, reference, verifyPayment]);

	if (!isOpen) return null;

	const startPayment = async () => {
		const payload = await initializePayment(eventId, { callbackUrl: window.location.href });
		if (payload?.alreadyActive) {
			dispatchPaymentSuccess({ eventId, reference: payload.reference || '' });
			onPaymentSuccess?.(payload);
			onClose?.();
			return;
		}

		setReference(payload.reference || '');
		setAmountNgn(payload.amountNgn || initialAmountNgn);
		setStatusMessage(
			payload.mockMode
				? 'Mock checkout opened. Complete it in the new tab, then this screen will refresh automatically.'
				: 'Checkout opened in a new tab. Complete payment there, then this screen will refresh automatically.'
		);

		if (payload.authorization_url) {
			popupRef.current = window.open(payload.authorization_url, '_blank', 'noopener,noreferrer');
			if (!popupRef.current) {
				window.location.assign(payload.authorization_url);
			}
		}
	};

	const createWhatsAppLink = async () => {
		const payload = await generatePaymentLink(eventId, { callbackUrl: window.location.href });
		setWhatsAppUrl(payload.whatsAppUrl || '');
		if (payload.whatsAppUrl) {
			window.open(payload.whatsAppUrl, '_blank', 'noopener,noreferrer');
		}
	};

	const confirmPayment = async () => {
		if (!reference) return;
		const verification = await verifyPayment(reference);
		if (verification?.status === 'success') {
			dispatchPaymentSuccess({ eventId, reference });
			onPaymentSuccess?.(verification);
			onClose?.();
			return;
		}
		setStatusMessage('Payment is still pending. Complete checkout, then try again.');
	};

	return (
		<div style={overlayStyle} role="dialog" aria-modal="true" aria-labelledby="payment-modal-title">
			<div style={modalStyle}>
				<h2 id="payment-modal-title" style={{ marginTop: 0 }}>Activate Event</h2>
				<p style={{ color: '#4B5563', lineHeight: 1.6 }}>
					Pay to unlock this event before going live. Eventory enforces monetization by table count, not guest count.
				</p>
				<div style={{ background: '#F9FAFB', borderRadius: '16px', padding: '16px', marginTop: '16px' }}>
					<div style={{ fontSize: '14px', color: '#6B7280' }}>Amount due</div>
					<div style={{ fontSize: '28px', fontWeight: 800, marginTop: '4px' }}>{formatNaira(amountNgn)}</div>
				</div>
				{statusMessage && <p style={{ color: '#111113', marginTop: '16px' }}>{statusMessage}</p>}
				{error && <p style={{ color: '#B91C1C', marginTop: '16px' }}>{error}</p>}
				{whatsAppUrl && (
					<p style={{ color: '#047857', marginTop: '16px', wordBreak: 'break-word' }}>
						WhatsApp payment link ready: {whatsAppUrl}
					</p>
				)}
				<div style={buttonRowStyle}>
					<button type="button" style={primaryButtonStyle} onClick={startPayment} disabled={loading}>
						{loading ? 'Opening Checkout...' : 'Pay Now'}
					</button>
					<button type="button" style={softButtonStyle} onClick={createWhatsAppLink} disabled={loading}>
						Share via WhatsApp
					</button>
					<button type="button" style={softButtonStyle} onClick={confirmPayment} disabled={loading || !isTrackingReference}>
						I&apos;ve Paid
					</button>
					<button type="button" style={softButtonStyle} onClick={onClose}>
						Not Now
					</button>
				</div>
			</div>
		</div>
	);
};

export default PaymentModal;
